import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../api/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  LifeBuoy, Loader2, AlertCircle, CheckCircle, Mail, ExternalLink, Filter,
} from 'lucide-react';
import { toast } from 'sonner';

const KIND_COLORS = {
  support:    'bg-sky-500/15 text-sky-300 border-sky-500/40',
  suggestion: 'bg-violet-500/15 text-violet-300 border-violet-500/40',
  bug:        'bg-rose-500/15 text-rose-300 border-rose-500/40',
  other:      'bg-slate-500/15 text-slate-300 border-slate-500/40',
};

export default function SupportInbox() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [status, setStatus] = useState('open');
  const [loading, setLoading] = useState(true);

  const isMaster = user?.role === 'super_admin';

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/api/support/messages?status=${status}&limit=200`);
      setItems(data.items || []);
      setOpenCount(data.open_count || 0);
    } catch (e) {
      if (e.response?.status === 403) {
        // Not master — silent
      } else {
        toast.error('Failed to load inbox');
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { if (isMaster) load(); else setLoading(false); }, [status, isMaster]);

  if (!isMaster) {
    return (
      <div className="space-y-4 fade-in">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <LifeBuoy className="w-7 h-7 text-sky-500" /> Support Inbox
        </h1>
        <Card>
          <CardContent className="p-6 flex items-start gap-3 text-amber-300">
            <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold">Master admin only</p>
              <p className="text-sm text-muted-foreground">This page is reserved for the platform owner.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const resolve = async (id) => {
    try {
      await apiClient.post(`/api/support/messages/${id}/resolve`);
      toast.success('Marked resolved');
      await load();
    } catch (e) { toast.error('Could not resolve'); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="support-inbox-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2 mb-1">
            <LifeBuoy className="w-7 h-7 text-sky-500" /> Support Inbox
            {openCount > 0 && (
              <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40 border ml-2">
                {openCount} open
              </Badge>
            )}
          </h1>
          <p className="text-muted-foreground">Messages and suggestions from users and visitors.</p>
        </div>
        <div className="flex gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40"><Filter className="w-4 h-4 mr-1" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-muted-foreground">
          {status === 'open' ? 'No open messages — inbox zero! 🎉' : 'No resolved messages yet.'}
        </CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {items.map((m) => {
            const kindCls = KIND_COLORS[m.kind] || KIND_COLORS.other;
            return (
              <Card key={m.id} className="card-hover" data-testid={`support-msg-${m.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${kindCls} uppercase tracking-wider`}>{m.kind}</span>
                        <CardTitle className="text-base">{m.subject || '(no subject)'}</CardTitle>
                      </div>
                      <CardDescription className="text-xs mt-1">
                        {m.name || 'Anonymous'}{m.email ? ` · ${m.email}` : ''}{m.phone ? ` · ${m.phone}` : ''}
                        {' · '}
                        {new Date(m.created_at).toLocaleString()}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      {m.email && (
                        <a href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject || 'Your message to My Key Boss')}`}>
                          <Button size="sm" variant="outline"><Mail className="w-3 h-3 mr-1" /> Reply</Button>
                        </a>
                      )}
                      {m.status === 'open' && (
                        <Button size="sm" onClick={() => resolve(m.id)} className="btn-gradient-success" data-testid={`resolve-${m.id}`}>
                          <CheckCircle className="w-3 h-3 mr-1" /> Resolve
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm whitespace-pre-wrap">{m.message}</p>
                  {m.page_url && (
                    <a href={m.page_url} target="_blank" rel="noreferrer" className="text-[11px] text-muted-foreground hover:text-sky-400 mt-2 flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> {m.page_url}
                    </a>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
